import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Button, Input, Modal, Segmented, Select, Space, Tooltip, Typography } from 'antd';
import { EditOutlined, ThunderboltOutlined, UndoOutlined } from '@ant-design/icons';
import { AI_REVISE_SUGGESTIONS, AI_TONES, isUsableLink, type EmailDesign } from '@crm/shared';
import { AiModelSelect } from '../AiModelSelect';
import { useAiRevise, useAiStatus, useAiWrite, type AiEmailResult } from '../../features/ai/api';
import { errorMessage } from '../../api/client';

interface Content {
  subject: string;
  design: EmailDesign;
}

interface Props extends Content {
  agentId?: string | null;
  /** receives the AI's version, or the previous version on undo */
  onApply: (next: Content) => void;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Write with AI" and "Revise with AI": the AI replaces subject, preview text and blocks; style is kept. Undo restores. */
export function AiEmailButtons({ subject, design, agentId, onApply }: Props) {
  const { message } = App.useApp();
  const status = useAiStatus();
  const write = useAiWrite();
  const revise = useAiRevise();
  const [open, setOpen] = useState<'write' | 'revise' | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [brief, setBrief] = useState('');
  const [tone, setTone] = useState<(typeof AI_TONES)[number]>('friendly');
  const [length, setLength] = useState<'short' | 'medium' | 'long'>('medium');
  const [buttonUrl, setButtonUrl] = useState('');
  const [instruction, setInstruction] = useState('');
  const [undo, setUndo] = useState<{ content: Content; model: string } | null>(null);

  const ready = !!status.data?.models.length;
  const chosen = model ?? status.data?.defaultModel ?? null;
  const modelLabel = (ref: string) => status.data?.models.find((m) => m.ref === ref)?.label ?? ref;
  const disabledWhy = status.isLoading ? 'Loading AI models…' : !ready ? 'AI is not set up yet' : undefined;
  const buttonUrlBad = !!buttonUrl.trim() && !isUsableLink(buttonUrl);

  const apply = (r: AiEmailResult) => {
    setUndo({ content: { subject, design }, model: r.model });
    onApply({ subject: r.subject || subject, design: { ...design, preheader: r.preheader, blocks: r.blocks } });
    setOpen(null);
  };
  const runWrite = () =>
    write
      .mutateAsync({ model: chosen ?? undefined, brief, tone, length, buttonUrl: buttonUrl.trim() || undefined, agentId })
      .then((r) => {
        apply(r);
        message.success('Email written. Check it before sending.');
      })
      .catch((e) => message.error(errorMessage(e)));
  const runRevise = () =>
    revise
      .mutateAsync({ model: chosen ?? undefined, instruction, content: { subject, preheader: design.preheader, blocks: design.blocks }, agentId })
      .then((r) => {
        apply(r);
        setInstruction('');
        message.success('Email revised');
      })
      .catch((e) => message.error(errorMessage(e)));

  const modelField = (
    <div style={{ marginTop: 16 }}>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Model</Typography.Text>
      <AiModelSelect status={status.data} value={chosen} onChange={setModel} loading={status.isLoading} />
    </div>
  );

  return (
    <>
      <Space wrap>
        <Tooltip title={disabledWhy}>
          <Button icon={<ThunderboltOutlined />} disabled={!ready} onClick={() => setOpen('write')}>Write with AI</Button>
        </Tooltip>
        <Tooltip title={disabledWhy ?? (!design.blocks.length ? 'Add some content first' : undefined)}>
          <Button icon={<EditOutlined />} disabled={!ready || !design.blocks.length} onClick={() => setOpen('revise')}>Revise with AI</Button>
        </Tooltip>
      </Space>
      {status.data && !ready && (
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
          To use AI, add a Claude or OpenAI key. See <Link to="/settings/general">Settings → General</Link>.
        </Typography.Text>
      )}
      {undo && (
        <Alert
          style={{ marginTop: 8 }}
          type="success"
          showIcon
          closable
          onClose={() => setUndo(null)}
          title={`Written by ${modelLabel(undo.model)}. Read it through before sending.`}
          action={
            <Button size="small" icon={<UndoOutlined />} onClick={() => { onApply(undo.content); setUndo(null); }}>
              Undo
            </Button>
          }
        />
      )}

      <Modal
        title="Write with AI"
        open={open === 'write'}
        onCancel={() => setOpen(null)}
        okText="Write email"
        okButtonProps={{ icon: <ThunderboltOutlined />, disabled: brief.trim().length < 3 || !chosen || buttonUrlBad }}
        confirmLoading={write.isPending}
        onOk={() => void runWrite()}
        width={620}
        destroyOnHidden={false}
      >
        <Typography.Paragraph type="secondary">Describe the email. The AI writes the subject, preview text and content; your logo, colours and footer stay as they are.</Typography.Paragraph>
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>What is it about?</Typography.Text>
        <Input.TextArea
          aria-label="What the email is about"
          autoFocus
          autoSize={{ minRows: 4, maxRows: 10 }}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder="e.g. Remind speakers that the conference is in two weeks, ask them to upload their slides by 1 October and confirm their travel."
          maxLength={4000}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Tone</Typography.Text>
            <Select aria-label="Tone" style={{ width: '100%' }} value={tone} onChange={setTone} options={AI_TONES.map((t) => ({ value: t, label: cap(t) }))} />
          </div>
          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Length</Typography.Text>
            <Segmented block value={length} onChange={(v) => setLength(v as typeof length)} options={[{ value: 'short', label: 'Short' }, { value: 'medium', label: 'Medium' }, { value: 'long', label: 'Long' }]} />
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Button link (optional)</Typography.Text>
          <Input aria-label="Button link" value={buttonUrl} onChange={(e) => setButtonUrl(e.target.value)} placeholder="https://… adds a button that goes here" status={buttonUrlBad ? 'error' : undefined} />
        </div>
        {modelField}
        {design.blocks.length > 0 && <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 12 }}>This replaces the current subject and content. You can undo it.</Typography.Text>}
      </Modal>

      <Modal
        title="Revise with AI"
        open={open === 'revise'}
        onCancel={() => setOpen(null)}
        okText="Revise email"
        okButtonProps={{ icon: <EditOutlined />, disabled: instruction.trim().length < 2 || !chosen }}
        confirmLoading={revise.isPending}
        onOk={() => void runRevise()}
        width={620}
      >
        <Typography.Paragraph type="secondary">Say what to change. Pictures, links and merge tags are kept unless you ask otherwise.</Typography.Paragraph>
        <Input.TextArea aria-label="What to change" autoFocus autoSize={{ minRows: 3, maxRows: 8 }} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="e.g. Make it shorter and mention the early-bird deadline of 15 September." maxLength={4000} />
        <div className="merge-chips" style={{ marginTop: 10 }}>
          {AI_REVISE_SUGGESTIONS.map((s) => (
            <Button key={s} size="small" onClick={() => setInstruction((cur) => (cur.trim() ? `${cur.trim()}\n${s}.` : `${s}.`))}>{s}</Button>
          ))}
        </div>
        {modelField}
      </Modal>
    </>
  );
}
